import { Form, Stack, Col, Grid, Row, IconButton, Divider, Button} from 'rsuite';
import { ActivityReport, ActivityReportTypeList } from '@/models/activity-report.model';
import PageContent from '@/components/PageContent';
import Textarea from '@/components/Textarea';
import { GoogleMap, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { getBoundingBoxCenter } from '@/utils/map';
import { hk_lat_lng } from '@/constants/map';
import { getHeight } from 'rsuite/esm/DOMHelper';
import { useNavigate } from 'react-router-dom';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import { utc_iana_time_zone_id } from '@/constants/time';
import React from 'react';
import { Icon } from '@rsuite/icons';
import { MdLink } from 'react-icons/md';
import { jsDateToZone } from '@/utils/date-time';

interface ActivityReportDetailProps{
  formObject?: ActivityReport;
};

const ActivityReportDetail: React.FC<ActivityReportDetailProps> = ({formObject}) => {

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_MAP_API_KEY!,
  });

  const nav = useNavigate();
  
  const getRouteStart = () => {
    if(formObject?.activity?.route && formObject?.activity?.route.length > 0){
      return formObject.activity.route[0];
    }
    return undefined;
  };

  const getRouteEnd = () => {
    if(formObject?.activity?.route && formObject?.activity?.route.length > 1){
      return formObject.activity.route[formObject.activity.route.length - 1];
    }
    return undefined;
  };
  const getMapCenter = () => {
    if(formObject?.activity?.route && formObject?.activity?.route.length > 1){
      return getBoundingBoxCenter(formObject.activity.route);
    } else if(formObject?.activity.city && formObject.activity.city.lat && formObject.activity.city.lon){
      return {lat: formObject.activity.city.lat, lng: formObject.activity.city.lon};
    }
    return hk_lat_lng;
  };
  
  const mapContainerStyle = { 
    width: '100%', 
    height: `${getHeight(window) - 350}px` , 
    borderRadius: 10 
  };

  const routePolylineOptions = {
    strokeColor: '#808000	', 
    strokeOpacity: 0.7,
    strokeWeight: 10,
    geodesic: true,
  }; 

  return (
    <>
      <PageContent>
        <Form fluid
          formValue={formObject}
          readOnly={true}
        >
          <Grid w='100%' fluid>
            <Row>
              <Col span={{xs:24, xl:8}} >
                <Row>
                  <Col span={{xs:24, xl:12}}>
                    <Form.Group>
                      <Form.Label>Country</Form.Label>
                      <Form.Control name="country" value={formObject?.activity.country?.name}/>                     
                    </Form.Group>
                  </Col>
                  <Col span={{xs:24, xl:12}}>
                    <Form.Group>
                      <Form.Label>City</Form.Label>
                      <Form.Control name="city" value={formObject?.activity.city?.name}/>                     
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col span={{xs:24, xl:12}}>
                    <Form.Group>
                      <Form.Label>Start Time ({formObject?.activity.city ? formObject.activity.city.ianaTimeZoneId : "UTC"})</Form.Label>
                      <Form.Control name="city" value={jsDateToZone(formObject?.activity.startTime, formObject?.activity.city?.ianaTimeZoneId ?? utc_iana_time_zone_id)}/>                     
                    </Form.Group>
                  </Col>
                    <Col span={{xs:24, xl:12}}>
                      <Form.Group>
                        <Form.Label>Duration</Form.Label>
                        <Form.Control name="duration" value={formObject?.activity.duration} />
                      </Form.Group>                     
                    </Col>
                </Row>
                <Row>
                  <Col span={{xs:24, xl:12}}>
                    <Form.Group>
                      <Form.Label>Type</Form.Label>
                      <Form.Control name="type" value={ActivityReportTypeList.findLast(t => t.value == formObject?.type)?.label}/>                     
                    </Form.Group>
                  </Col>
                  <Col span={{xs:24, xl:12}}>
                    <Form.Group>
                      <Form.Label>Distance (Meter)</Form.Label>
                      <Form.Control name="distance" value={formObject?.activity.distanceInMeter} />
                    </Form.Group>                    
                  </Col>
                </Row>
                <Row>
                  <Col span={{xs:24, xl:12}}>
                    <Form.Group>
                      <Form.Label>Start Latitude</Form.Label>
                      <Form.Control name="startLat" value={getRouteStart()?.lat} />
                    </Form.Group>                  
                  </Col>
                  <Col span={{xs:24, xl:12}}>
                    <Form.Group>
                      <Form.Label>Start Longitude</Form.Label>
                      <Form.Control name="startLon" value={getRouteStart()?.lng} />
                    </Form.Group>                  
                  </Col>
                </Row>
                <Row>
                  <Col span={{xs:24, xl:12}}>
                    <Form.Group>
                      <Form.Label>End Latitude</Form.Label>
                      <Form.Control name="endLat" value={getRouteEnd()?.lat} />
                    </Form.Group>                     
                  </Col>
                  <Col span={{xs:24, xl:12}}>
                    <Form.Group>
                      <Form.Label>End Longitude</Form.Label>
                      <Form.Control name="endLon" value={getRouteEnd()?.lng} />
                    </Form.Group>                    
                  </Col>
                </Row>
                <Form.Group>
                  <Form.Label>Report Description</Form.Label>
                  <Form.Control name="description"
                    value={formObject?.description } 
                    accepter={Textarea}
                    rows={15}
                  /> 
                </Form.Group> 
                <Button startIcon={<Icon as={MdLink} /> } onClick={() => nav('/activities/' + formObject?.activityId)} block>Go To Activity Page</Button>
              </Col>
              <Col span={{xs:24, xl:16}} style={{textAlign: 'center'}}>
                <Form.Group>
                  <Form.Label>Route</Form.Label>
                  {isLoaded ? <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center = {getMapCenter()}
                    zoom={15}
                  >
                    {formObject?.activity && formObject.activity.route && formObject.activity.route.length > 1 && <Polyline
                      path={formObject.activity.route}
                      options={routePolylineOptions}
                    />}

                  </GoogleMap> : '' }                                  
                </Form.Group>
              </Col>
            </Row>
          </Grid>
        </Form>

        <Divider />

        <Stack justifyContent="space-between">
          <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
            Back
          </IconButton>
        </Stack>
      </PageContent>
    </>
  );
};

export default ActivityReportDetail;
